import React from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import WideContainer from "../../components/WideContainer";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Popover from "@mui/material/Popover";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import CircularProgress from "@mui/material/CircularProgress";
import { Player, ControlBar, BigPlayButton, LoadingSpinner, PlayToggle, VolumeMenuButton, FullscreenToggle, CurrentTimeDisplay, DurationDisplay, ProgressControl } from "video-react";
import { Trans, withTranslation } from "react-i18next";

import "../../../node_modules/video-react/dist/video-react.css";
import axios from "../../components/Axios";
import { fetchSentences } from "../../slices/sentences";
import { fmt, memoize } from "./scenes/sceneModel";
import { Hl } from "./scenes/SceneCard";
import { isOther } from "./tracks/tracksModel";
import { pressable } from "../../app/a11y";
import "./Queries.css";
import "./Scenes.css";
import "./Transcript.css";

// one transcript row (same design as the Scenes transcript); pure so player ticks only re-render rows that change
class TranscriptLine extends React.PureComponent {
  seek = () => this.props.onSeek(this.props.s.start_time);
  editChar = (e) => this.props.onEdit(this.props.s, "char", this.props.i, e);
  editText = (e) => this.props.onEdit(this.props.s, "text", this.props.i, e);
  setRef = (ref) => this.props.setRef(this.props.i, ref);

  render() {
    const { s, name, active, q } = this.props;
    return (
      <div className="sc-line" data-on={active} onClick={this.seek} ref={this.setRef}>
        <span>{fmt(s.start_time)}</span>
        <span className="tr-who">
          <span className="sc-ellipsis" title={name}>
            <Hl text={name} q={q} />
          </span>
          <IconButton className="tr-edit" size="small" onClick={this.editChar}>
            <EditIcon />
          </IconButton>
        </span>
        <span>
          <Hl text={s.text} q={q} />
          <IconButton className="tr-edit" size="small" onClick={this.editText}>
            <EditIcon />
          </IconButton>
        </span>
      </div>
    );
  }
}

class Index extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      items: [],
      source: 0,
      queries: [],
      query: 0,
      sentences: [],
      characters: {},
      edit_el: null,
      edit_index: null,
      edit_text: "",
      edit_char: "",
      edit_type: "",
      player: {
        duration: 0,
      },
      playerSource: undefined,
      lastScroll: 0,
      selected_char: null,
      search: "",
      originalChecked: false,
      originalSrc: null,
      originalFailed: false,
      showSilent: false, // list the characters who say nothing too (faces from the Tracks tab)
    };

    this.sentenceRef = [];
    this.editInput = React.createRef();
    this.loadTranscript = this.loadTranscript.bind(this);
    this.seekVideo = this.seekVideo.bind(this);
    this.openEdit = this.openEdit.bind(this);
    this.setSentenceRef = this.setSentenceRef.bind(this);

    // which sentences pass the character filter and the search, and how many search hits they hold
    this.getVisible = memoize((items, dic, selected, q) => {
      const count = (v) => (q ? String(v || "").toLowerCase().split(q).length - 1 : 0);
      let hits = 0;
      const visible = items.map((s) => {
        if (selected !== null && s.char_id !== selected) return false;
        if (!q) return true;
        const n = count(s.text) + count(dic[s.char_id]);
        hits += n;
        return n > 0;
      });
      return { visible, hits };
    });

    // the characters list: most lines first, the Others bucket last; those with no line hidden unless asked
    // (or selected), they are faces without a voice
    this.getChars = memoize((items, speech, showSilent, selected) => {
      const lines = (c) => (speech[c.char_id] || { lines: 0 }).lines;
      const sorted = (items || []).slice().sort((a, b) => Number(isOther(a)) - Number(isOther(b)) || lines(b) - lines(a));
      const silent = sorted.filter((c) => lines(c) === 0 && c.char_id !== selected).length;
      return { list: showSilent ? sorted : sorted.filter((c) => lines(c) > 0 || c.char_id === selected), silent };
    });

    // transcript lines and speech time per character
    this.getSpeech = memoize((items) => {
      const m = {};
      items.forEach((s) => {
        const c = m[s.char_id] || (m[s.char_id] = { lines: 0, secs: 0 });
        c.lines++;
        c.secs += Math.max(0, s.end_time - s.start_time);
      });
      return m;
    });
  }

  visible() {
    return this.getVisible(this.props.sentences.items, this.props.characters.dic, this.state.selected_char, this.state.search.trim().toLowerCase());
  }

  setSearch = (e) => this.setState({ search: e.target.value });
  clearSearch = () => this.setState({ search: "" });
  searchKey = (e) => e.key === "Escape" && this.clearSearch();

  componentDidMount() {
    // subscribe state change
    this.player.subscribeToStateChange(this.handleStateChange.bind(this));

    //play the original upload; fall back to the low resolution video (props.videosrc) if there is none
    axios
      .get("/video_project/" + this.props.project.project_id + "/videos?video_type=ORIGINAL")
      .then(
        function (response) {
          this.setState({ originalChecked: true, originalSrc: 0 in response.data ? response.data[0].video_path : null });
        }.bind(this),
      )
      .catch(
        function (error) {
          console.log(error);
          this.setState({ originalChecked: true });
        }.bind(this),
      );
  }

  loadTranscript() {
    this.props.dispatch(fetchSentences(this.props.project.project_id));
    return;
  }

  videoSrc(props, state) {
    if (!state.originalChecked) return undefined;
    return (!state.originalFailed && state.originalSrc) || props.videosrc;
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.videoSrc(this.props, this.state) !== this.videoSrc(prevProps, prevState)) {
      this.player.load();
    }
  }

  setSentenceRef(i, ref) {
    this.sentenceRef[i] = ref;
  }

  handleStateChange(state) {
    // copy player state to this component's state
    this.setState({
      player: state,
    });
    // share the time with the other tabs (once the video has a time of its own)
    if (this.props.onTime && (state.hasStarted || state.currentTime > 0)) this.props.onTime(state.currentTime);

    //the browser can't play the original upload (e.g. mkv): use the low resolution video
    if (state.error && this.state.originalSrc && !this.state.originalFailed) {
      console.log("original video failed to play, using low resolution", state.error);
      this.setState({ originalFailed: true });
      return;
    }

    //do not scroll when paused
    if (state.paused) return;
    //do not scroll when editing
    if (this.state.edit_el !== null) return;
    //do not scroll when user scrolled in the last 3 seconds
    if (new Date().getTime() - this.state.lastScroll < 3000) return;

    const visible = this.visible().visible;
    for (var i in this.props.sentences.items) {
      //skip sentences hidden by the character filter or the search
      if (!visible[i]) continue;
      if (this.props.sentences.items[i].start_time - 0.5 < state.currentTime && state.currentTime < this.props.sentences.items[i].end_time) {
        this.sentenceRef[i].scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "nearest",
        });
        break;
      }
    }
  }

  handleChange(e) {
    this.setState({ [e.target.name]: e.target.value });
  }

  openEdit(s, t, i, e) {
    e.stopPropagation();

    this.setState({
      edit_el: e.currentTarget,
      edit_index: i,
      edit_char: s.char_id,
      edit_text: s.text,
      edit_type: t,
    });
  }
  closeEdit() {
    this.setState({
      edit_el: null,
      edit_id: null,
    });
  }
  // focus the field once the popover has opened: in StrictMode (dev) the popover's focus trap takes autoFocus back
  focusEdit = () => this.editInput.current && this.editInput.current.focus();
  // Enter saves the line; Shift+Enter still adds a line break
  editKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      this.saveEdit();
    }
  };
  saveEdit(e) {
    if (e) e.preventDefault();
    let d = Object.assign({}, this.props.sentences.items[this.state.edit_index]);
    d.char_id = this.state.edit_char;
    d.text = this.state.edit_text;
    d.index = parseInt(d.index);

    this.setState({
      edit_el: null,
      edit_id: null,
    });
    axios
      .put("/video_project/" + this.props.project.project_id + "/sentences", d)
      .then(
        function (response) {
          this.loadTranscript();
        }.bind(this),
      )
      .catch(
        function (error) {
          console.log(error);
          //this.setState({ loading: false });
        }.bind(this),
      );
  }

  seekVideo(t) {
    this.player.seek(t);
  }

  // the video's thumbnail, shown until it plays (the first frame is often black or color bars)
  posterUrl() {
    return JSON.parse(localStorage.getItem("user")).user_folder + "/" + this.props.project.project_folder + "/video_lowres_thumbnail.jpg";
  }

  getThumbUrl(i) {
    if (i === -1) return "/img/people.png";
    return JSON.parse(localStorage.getItem("user")).user_folder + "/" + this.props.project.project_folder + "/tracks_thumbnails/" + i + ".jpg";
  }

  toggleCharacter(char_id) {
    this.setState((state) => ({
      selected_char: state.selected_char === char_id ? null : char_id,
    }));
  }

  getSelectedCharTracks() {
    let c = this.props.characters.items.find((i) => i.char_id === this.state.selected_char);
    return c ? c.tracks_data : [];
  }

  getTimelineStyle(s, d) {
    let w = ((s.end_time - s.start_time) / d) * 100;
    let l = (s.start_time / d) * 100;

    return { left: l + "%", width: w + "%" };
  }

  render() {
    const { t } = this.props;
    const items = this.props.sentences.items;
    const q = this.state.search.trim().toLowerCase();
    const { visible, hits } = this.visible();
    const speech = this.getSpeech(items);
    const chars = this.getChars(this.props.characters.items, speech, this.state.showSilent, this.state.selected_char);
    const now = this.state.player.hasStarted ? this.state.player.currentTime : -1;
    let active = -1;
    items.forEach((s, i) => {
      if (visible[i] && s.start_time <= now + 0.01) active = i;
    });
    const editChar = this.state.edit_type === "char";
    const editLine = items[this.state.edit_index];

    return (
      <>
        <WideContainer className="transcriptTab">
          <div className="tr-chars">
            <div className="tr-chars-head">
              <span className="sc-overline">
                <Trans>Characters</Trans>
              </span>
              <IconButton component={Link} to="tracks" size="small" title={t("Edit")}>
                <EditIcon />
              </IconButton>
            </div>
            {this.state.loading && <CircularProgress style={{ margin: "40px auto", display: "block" }} />}
            <div className="tr-chars-list">
              {this.props.characters.loading === "idle" &&
                chars.list.map((i) => {
                  const sp = speech[i.char_id] || { lines: 0, secs: 0 };
                  const meta = [sp.lines === 1 ? t("1 line") : t("{{count}} lines", { count: sp.lines }), t("{{time}} speech", { time: fmt(sp.secs) })];
                  if (i.char_actor && i.char_actor !== i.char_name) meta.push(i.char_actor);
                  return (
                    <div
                      className="tr-char"
                      data-on={i.char_id === this.state.selected_char}
                      key={i.char_id}
                      {...pressable(this.toggleCharacter.bind(this, i.char_id), { "aria-pressed": i.char_id === this.state.selected_char })}
                    >
                      <span className="tr-char-img" style={{ backgroundImage: "url('" + this.getThumbUrl(i.rep_track_id) + "')" }} />
                      <div className="tr-char-text">
                        <div className="tr-char-name sc-ellipsis" title={i.char_name}>
                          {i.char_name}
                        </div>
                        <div className="tr-char-meta sc-ellipsis" title={meta.join(" · ")}>
                          {meta.join(" · ")}
                        </div>
                      </div>
                    </div>
                  );
                })}
              {this.props.characters.loading === "idle" && chars.silent > 0 && (
                <button type="button" className="tr-silent" onClick={() => this.setState((st) => ({ showSilent: !st.showSilent }))}>
                  {this.state.showSilent
                    ? t("Hide characters without lines")
                    : chars.silent === 1
                      ? t("Show 1 character without lines")
                      : t("Show {{count}} characters without lines", { count: chars.silent })}
                </button>
              )}
            </div>
          </div>
          <div className="tr-main">
            <Player
              ref={(player) => {
                this.player = player;
              }}
              autoPlay={false}
              startTime={this.props.startTime}
              poster={this.props.startTime > 0 ? undefined : this.posterUrl()}
              src={this.videoSrc(this.props, this.state)}
            >
              <BigPlayButton position="center" />
              <LoadingSpinner />
              <ControlBar autoHide={false} disableDefaultControls>
                <PlayToggle key="play-toggle" order={1} />
                <VolumeMenuButton key="volume-menu-button" order={4} vertical />
                <CurrentTimeDisplay key="current-time-display" order={5.1} />
                <DurationDisplay key="duration-display" order={7.3} />
                <ProgressControl key="progress-control" order={6} />
                <FullscreenToggle key="fullscreen-toggle" order={8} />
              </ControlBar>
            </Player>
            <div className="timelineHighlights">
              {this.state.player.hasStarted &&
                this.getSelectedCharTracks().map((t, k) => <div key={"highlight" + k} className="timelineHighlight" style={this.getTimelineStyle(t, this.state.player.duration)} />)}
            </div>
            <div className="tr-search">
              <div className="sc-search">
                <SearchIcon />
                <input value={this.state.search} onChange={this.setSearch} onKeyDown={this.searchKey} placeholder={t("Search transcript")} />
                {this.state.search && (
                  <IconButton className="sc-clear" size="small" title={t("Clear")} onClick={this.clearSearch}>
                    <CloseIcon />
                  </IconButton>
                )}
              </div>
              {q && <span className="tr-search-count">{hits === 1 ? t("1 match") : t("{{count}} matches", { count: hits })}</span>}
            </div>
            <div
              className="tr-lines"
              onScroll={() => {
                this.setState({
                  lastScroll: new Date().getTime(),
                });
              }}
            >
              {this.props.sentences.loading === "pending" && <CircularProgress style={{ margin: "40px auto", display: "block" }} />}
              {q && hits === 0 && <div className="sc-empty">{t("No results found")}</div>}
              {items.map(
                (s, i) =>
                  visible[i] && (
                    <TranscriptLine
                      key={"sentence" + s.sentence_id}
                      s={s}
                      i={i}
                      q={q}
                      name={this.props.characters.dic[s.char_id]}
                      active={i === active}
                      onSeek={this.seekVideo}
                      onEdit={this.openEdit}
                      setRef={this.setSentenceRef}
                    />
                  ),
              )}
            </div>
          </div>
        </WideContainer>
        <Popover
          open={this.state.edit_el === null ? false : true}
          anchorEl={this.state.edit_el}
          anchorOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
          transformOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
          onClose={this.closeEdit.bind(this)}
          TransitionProps={{ onEntered: this.focusEdit }}
          slotProps={{ paper: { className: "tr-editPop" + (editChar ? " tr-editPop--char" : "") } }}
        >
          {/* compact like the Tracks tab's inline rename: Enter saves, Esc cancels */}
          <form className="tr-editForm" onSubmit={this.saveEdit.bind(this)}>
            <div className="tr-editHead">
              <span className="sc-overline">{editChar ? t("Character") : t("Text")}</span>
              {editLine && <span className="tr-editTime">{fmt(editLine.start_time)}</span>}
            </div>
            {editChar ? (
              <Select
                size="small"
                fullWidth
                inputRef={this.editInput}
                name="edit_char"
                value={this.state.edit_char}
                onChange={this.handleChange.bind(this)}
                MenuProps={{ className: "tr-editMenu" }}
                inputProps={{ "aria-label": t("Character") }}
              >
                {Object.keys(this.props.characters.dic).map((i) => (
                  <MenuItem key={"char_" + i} value={parseInt(i)}>
                    {this.props.characters.dic[i]}
                  </MenuItem>
                ))}
              </Select>
            ) : (
              <TextField
                fullWidth
                inputRef={this.editInput}
                multiline
                size="small"
                maxRows={4}
                name="edit_text"
                value={this.state.edit_text}
                onChange={this.handleChange.bind(this)}
                onKeyDown={this.editKey}
                slotProps={{ htmlInput: { "aria-label": t("Text") } }}
              />
            )}
            <div className="tr-editBtns">
              <Button className="Button" size="small" onClick={this.closeEdit.bind(this)}>
                {t("Cancel")}
              </Button>
              <Button type="submit" className="Button ButtonPrimary" size="small">
                {t("Save")}
              </Button>
            </div>
          </form>
        </Popover>
      </>
    );
  }
}

const mapState = (state) => {
  return {
    sentences: state.sentences,
    characters: state.characters,
  };
};
export default withTranslation()(connect(mapState)(Index));
